import React, { useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/** Parse "YYYY-MM-DD" to { year, month (1-based), day } */
function parseIso(iso) {
  if (!iso) return null;
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return null;
  return { year: y, month: m, day: d };
}

/** Format { year, month, day } to "YYYY-MM-DD" */
function toIso({ year, month, day }) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Format YYYY-MM-DD to DD/MM/YYYY for manual input */
function formatDDMMYYYY(iso) {
  if (!iso) return '';
  const p = parseIso(iso);
  if (!p) return '';
  return `${String(p.day).padStart(2, '0')}/${String(p.month).padStart(2, '0')}/${p.year}`;
}

/** Parse DD/MM/YYYY to YYYY-MM-DD */
function parseDDMMYYYY(ddmmyyyy) {
  if (!ddmmyyyy) return null;
  const match = String(ddmmyyyy).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;
  const [, day, month, year] = match;
  const d = parseInt(day, 10);
  const m = parseInt(month, 10);
  const y = parseInt(year, 10);
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  // Validate it's a real date
  const date = new Date(y, m - 1, d);
  if (date.getDate() !== d || date.getMonth() !== m - 1 || date.getFullYear() !== y) return null;
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Build the day grid for a given year/month (Sun-start) */
function buildMonthGrid(year, month) {
  const firstDay = new Date(year, month - 1, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];
  // Leading nulls
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  // Trailing nulls to complete last row
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function addMonths(year, month, delta) {
  let m = month + delta;
  let y = year;
  while (m > 12) { m -= 12; y++; }
  while (m < 1) { m += 12; y--; }
  return { year: y, month: m };
}

/**
 * DatePickerInput — premium custom date picker.
 * Props:
 *   value      : "YYYY-MM-DD" string (controlled)
 *   onChange   : (value: string) => void
 *   label      : string
 *   id         : string
 *   name       : string (used in synthetic event for handleInputChange compatibility)
 *   minDate    : "YYYY-MM-DD" string
 *   maxDate    : "YYYY-MM-DD" string
 *   disabled   : bool
 *   required   : bool
 *   className  : string (for the wrapper)
 *   hasError   : bool
 *   allowManualInput : bool (default true) - allow typing DD/MM/YYYY
 * 
 * Ref methods (via ref):
 *   openCalendar() - programmatically open the calendar picker
 */
const DatePickerInput = React.forwardRef(function DatePickerInput({
  value,
  onChange,
  id,
  name,
  label,
  minDate,
  maxDate,
  disabled = false,
  required = false,
  className = '',
  hasError = false,
  allowManualInput = true,
}, ref) {
  const [open, setOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [openLeft, setOpenLeft] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  const [inputValue, setInputValue] = useState('');
  const [validationError, setValidationError] = useState('');
  const containerRef = useRef(null);
  const textInputRef = useRef(null);
  const calendarRef = useRef(null);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    openCalendar: () => {
      if (!disabled) {
        setOpen(true);
      }
    }
  }));


  // Current calendar view state
  const parsed = useMemo(() => parseIso(value), [value]);
  const minParsed = useMemo(() => parseIso(minDate), [minDate]);
  const maxParsed = useMemo(() => parseIso(maxDate), [maxDate]);
  const today = useMemo(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  }, []);

  // Initialize view to selected date, or maxDate (for DOB), or today
  const [viewYear, setViewYear] = useState(() => {
    if (parsed) return parsed.year;
    if (maxParsed) return maxParsed.year; // Default to maxDate for DOB pickers
    if (minParsed) return minParsed.year;
    return today.year;
  });
  const [viewMonth, setViewMonth] = useState(() => {
    if (parsed) return parsed.month;
    if (maxParsed) return maxParsed.month;
    if (minParsed) return minParsed.month;
    return today.month;
  });

  // Sync view when value changes externally
  useEffect(() => {
    if (parsed) {
      setViewYear(parsed.year);
      setViewMonth(parsed.month);
    }
  }, [parsed]);

  // Restore cursor position after state updates
  useEffect(() => {
    if (!open) return undefined;
    const handle = (e) => {
      // Check if click is outside both the input container and the calendar
      if (
        containerRef.current && 
        !containerRef.current.contains(e.target) &&
        calendarRef.current &&
        !calendarRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return undefined;
    const handle = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        textInputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open]);

  // Detect available space and pick best direction before paint (no flicker)
  // Smart calendar placement: left on desktop, below/above on mobile
  useLayoutEffect(() => {
    if (!open || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const CALENDAR_HEIGHT = 340;
    const CALENDAR_WIDTH = 308;
    const isMobile = window.innerWidth < 768;
    const spaceLeft = rect.left;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (!isMobile && spaceLeft >= CALENDAR_WIDTH + 8) {
      // Desktop: open to the left, vertically centered
      const inputMidY = rect.top + rect.height / 2;
      const calTop = inputMidY - CALENDAR_HEIGHT / 2;
      const clampedTop = Math.max(8, Math.min(calTop, window.innerHeight - CALENDAR_HEIGHT - 8));
      setOpenUpward(false);
      setOpenLeft(true);
      setCalendarPosition({
        top: clampedTop,
        left: rect.left - CALENDAR_WIDTH - 8,
      });
    } else if (!isMobile) {
      // Desktop: not enough left space, open below or above input
      setOpenLeft(false);
      const shouldOpenUpward = spaceAbove > spaceBelow && spaceBelow < CALENDAR_HEIGHT;
      setOpenUpward(shouldOpenUpward);
      setCalendarPosition({
        top: shouldOpenUpward ? rect.top - CALENDAR_HEIGHT - 4 : rect.bottom + 4,
        left: rect.left,
      });
    } else {
      // Mobile: use fixed centered positioning (handled by CSS)
      setOpenLeft(false);
      setOpenUpward(false);
      setCalendarPosition({ top: 0, left: 0 });
    }
  }, [open]);

  const cells = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);

  const isDisabledDay = (day) => {
    if (!day) return true;
    const cellIso = toIso({ year: viewYear, month: viewMonth, day });
    if (minParsed && cellIso < toIso(minParsed)) return true;
    if (maxParsed && cellIso > toIso(maxParsed)) return true;
    return false;
  };

  const isToday = (day) => {
    if (!day) return false;
    return viewYear === today.year && viewMonth === today.month && day === today.day;
  };

  const isSelected = (day) => {
    if (!day || !parsed) return false;
    return viewYear === parsed.year && viewMonth === parsed.month && day === parsed.day;
  };

  const handleDayClick = (day) => {
    if (!day || isDisabledDay(day)) return;
    const iso = toIso({ year: viewYear, month: viewMonth, day });
    // Clear any validation error when selecting from calendar
    setValidationError('');
    // Emit a synthetic event compatible with handleInputChange(e)
    onChange({ target: { name, value: iso } });
    setOpen(false);
    setInputValue('');
    // Focus the input after selection
    setTimeout(() => {
      textInputRef.current?.focus();
    }, 0);
  };

  const handleInputChange = (e) => {
    const input = e.target;
    let value = input.value;
    
    // Clear error when user starts typing
    if (validationError) {
      setValidationError('');
    }
    
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Auto-format with slashes as user types
    let formatted = '';
    if (digits.length > 0) {
      formatted = digits.slice(0, 2); // DD
      if (digits.length >= 3) {
        formatted += '/' + digits.slice(2, 4); // MM
      }
      if (digits.length >= 5) {
        formatted += '/' + digits.slice(4, 8); // YYYY
      }
    }
    
    setInputValue(formatted);
    
    // Try to sync calendar view if we can parse month/year
    if (formatted.length >= 5) {
      const parts = formatted.split('/');
      if (parts.length >= 2) {
        const month = parseInt(parts[1], 10);
        if (month >= 1 && month <= 12) {
          setViewMonth(month);
          if (parts.length >= 3 && parts[2].length === 4) {
            const year = parseInt(parts[2], 10);
            if (year >= 1900 && year <= 2100) {
              setViewYear(year);
            }
          }
        }
      }
    }
    
    // Validate and update only when complete (10 chars: DD/MM/YYYY)
    if (formatted.length === 10) {
      const iso = parseDDMMYYYY(formatted);
      if (!iso) {
        setValidationError('Please enter a valid date (DD/MM/YYYY).');
        return;
      }
      
      // Check bounds
      if (minParsed && iso < toIso(minParsed)) {
        setValidationError(`Date must be on or after ${formatDDMMYYYY(minDate)}.`);
        return;
      }
      
      if (maxParsed && iso > toIso(maxParsed)) {
        setValidationError(`Date must be on or before ${formatDDMMYYYY(maxDate)}.`);
        return;
      }
      
      // Date is valid - update parent
      setValidationError('');
      onChange({ target: { name, value: iso } });
    }
  };

  const handleInputBlur = () => {
    // If there's a partial input, try to validate and clear if invalid
    if (inputValue && inputValue.length > 0 && inputValue.length < 10) {
      setValidationError('Please enter a complete date (DD/MM/YYYY).');
      setTimeout(() => {
        setInputValue('');
        setValidationError('');
      }, 2000);
    } else if (!inputValue && !value) {
      // Clear validation error if input is empty and no value is set
      setValidationError('');
    }
  };

  const handleInputFocus = () => {
    // Pre-fill with current value if exists
    if (value && !inputValue) {
      const formatted = formatDDMMYYYY(value);
      setInputValue(formatted);
    }
  };

  const handleMonthChange = (e) => {
    setViewMonth(parseInt(e.target.value, 10));
  };

  const handleYearChange = (e) => {
    setViewYear(parseInt(e.target.value, 10));
  };

  const handlePrevMonth = () => {
    const { year, month } = addMonths(viewYear, viewMonth, -1);
    // Don't go before minDate month if minDate is set
    if (minParsed) {
      const newIso = `${year}-${String(month).padStart(2, '0')}-01`;
      const minIso = `${minParsed.year}-${String(minParsed.month).padStart(2, '0')}-01`;
      if (newIso < minIso) return;
    }
    setViewYear(year);
    setViewMonth(month);
  };
  const handleNextMonth = () => {
    const { year, month } = addMonths(viewYear, viewMonth, 1);
    setViewYear(year);
    setViewMonth(month);
  };

  // Check if prev button should be disabled
  const isPrevMonthDisabled = useMemo(() => {
    if (!minParsed) return false;
    const currentIso = `${viewYear}-${String(viewMonth).padStart(2, '0')}-01`;
    const minIso = `${minParsed.year}-${String(minParsed.month).padStart(2, '0')}-01`;
    return currentIso <= minIso;
  }, [minParsed, viewYear, viewMonth]);

  // Generate year options (105 years back from today for DOB support)
  const yearOptions = useMemo(() => {
    const years = [];
    const startYear = maxParsed ? maxParsed.year : today.year;
    const endYear = minParsed ? minParsed.year : today.year - 105;
    for (let y = startYear; y >= endYear; y--) {
      years.push(y);
    }
    return years;
  }, [minParsed, maxParsed, today.year]);

  // Display value: use inputValue while typing, or formatted value from prop
  const displayInputValue = inputValue || (value ? formatDDMMYYYY(value) : '');

  // Handle calendar icon click - sync view with typed date if available
  const handleCalendarIconClick = () => {
    if (disabled) return;
    
    // Try to sync calendar view with partially typed date
    if (inputValue && inputValue.length >= 5) {
      const parts = inputValue.split('/');
      if (parts.length >= 2) {
        const month = parseInt(parts[1], 10);
        if (parts.length >= 3 && parts[2].length === 4) {
          const year = parseInt(parts[2], 10);
          if (month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
            setViewMonth(month);
            setViewYear(year);
          }
        } else if (month >= 1 && month <= 12) {
          setViewMonth(month);
        }
      }
    }
    
    setOpen((o) => !o);
  };

  return (
    <div
      className={`dp-wrap${open ? ' dp-wrap--open' : ''}${disabled ? ' dp-wrap--disabled' : ''}${className ? ` ${className}` : ''}`}
      ref={containerRef}
    >
      <div className="dp-input-wrapper">
        <input
          ref={textInputRef}
          type="text"
          id={id}
          className={`dp-input${hasError || validationError ? ' input-invalid' : ''}`}
          value={displayInputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder="DD/MM/YYYY"
          disabled={disabled}
          maxLength={10}
          autoComplete="off"
        />
        <button
          type="button"
          className="dp-calendar-icon-btn"
          onClick={handleCalendarIconClick}
          disabled={disabled}
          aria-label="Open calendar"
          tabIndex={-1}
        >
          <span style={{ fontSize: '16px' }}>📅</span>
        </button>
      </div>

      {validationError && (
        <p className="field-error-text">{validationError}</p>
      )}

      {open && createPortal(
        <>
          {/* Mobile backdrop overlay */}
          <div 
            className="dp-backdrop"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          
          <div
            id={`${id}-calendar`}
            className={[
              'dp-calendar',
              openUpward ? 'dp-calendar--upward' : '',
              openLeft ? 'dp-calendar--left' : '',
            ].filter(Boolean).join(' ')}
            role="dialog"
            aria-modal="false"
            aria-label="Date picker"
            ref={calendarRef}
            style={
              window.innerWidth >= 768
                ? { top: `${calendarPosition.top}px`, left: `${calendarPosition.left}px` }
                : undefined
            }
          >
          {/* Calendar Label Header — shown compactly in header row */}
          {label && (
            <div style={{ textAlign: 'center', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {label}
            </div>
          )}
          {/* Month / Year header with dropdowns */}
          <div className="dp-calendar__header">
            <button
              type="button"
              className="dp-calendar__nav"
              onClick={handlePrevMonth}
              disabled={isPrevMonthDisabled}
              aria-label="Previous month"
            >
              ‹
            </button>
            <div className="dp-calendar__selectors">
              <select
                className="dp-calendar__month-select"
                value={viewMonth}
                onChange={handleMonthChange}
                aria-label="Month"
              >
                {MONTHS.map((m, idx) => (
                  <option key={m} value={idx + 1}>{m}</option>
                ))}
              </select>
              <select
                className="dp-calendar__year-select"
                value={viewYear}
                onChange={handleYearChange}
                aria-label="Year"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="dp-calendar__nav"
              onClick={handleNextMonth}
              aria-label="Next month"
            >
              ›
            </button>
          </div>

          {/* Day-of-week headers */}
          <div className="dp-calendar__days-header" aria-hidden="true">
            {DAYS.map((d) => (
              <span key={d} className="dp-calendar__day-label">{d}</span>
            ))}
          </div>

          {/* Day grid */}
          <div className="dp-calendar__grid" role="grid" aria-label={`${MONTHS[viewMonth - 1]} ${viewYear}`}>
            {cells.map((day, idx) => {
              const disabled_ = isDisabledDay(day);
              const selected_ = isSelected(day);
              const today_ = isToday(day);
              return (
                <button
                  // eslint-disable-next-line react/no-array-index-key
                  key={idx}
                  type="button"
                  role="gridcell"
                  tabIndex={day && !disabled_ ? 0 : -1}
                  aria-label={day ? `${day} ${MONTHS[viewMonth - 1]} ${viewYear}${selected_ ? ', selected' : ''}${today_ ? ', today' : ''}` : undefined}
                  aria-selected={selected_ || undefined}
                  aria-disabled={disabled_ || !day || undefined}
                  disabled={!day || disabled_}
                  className={[
                    'dp-calendar__day',
                    !day ? 'dp-calendar__day--empty' : '',
                    selected_ ? 'dp-calendar__day--selected' : '',
                    today_ && !selected_ ? 'dp-calendar__day--today' : '',
                    disabled_ ? 'dp-calendar__day--disabled' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => handleDayClick(day)}
                >
                  {day || ''}
                </button>
              );
            })}
          </div>
        </div>
        </>,
        document.body
      )}
    </div>
  );
});

export default DatePickerInput;
